/**
 * lib/posthog-exception-filter.ts — drop unactionable browser noise before it
 * reaches PostHog Error Tracking. Three independent classes are suppressed:
 *
 * 1. Anonymized cross-origin "Script error." (GitHub #795 — PostHog watchdog P1
 *    "(unknown error)"): `posthog.startExceptionAutocapture()`
 *    (components/analytics/posthog-provider.tsx) hooks window.onerror /
 *    onunhandledrejection / console.error. When a THIRD-PARTY cross-origin
 *    script throws — Cloudflare Turnstile on the /site/[token] gate page, plus
 *    Clerk, Stripe, GTM, Microsoft Clarity, Meta Pixel — the browser redacts the
 *    event for security: window.onerror receives a bare "Script error." with no
 *    message, no stack, no source. PostHog has nothing to fingerprint, so every
 *    such hit collapses into a single "(unknown error)" Issue, which the
 *    marketing-runtime watchdog then files as a P1 with no stack to act on.
 *    Dropped ONLY when EVERY entry in `$exception_list` is both (a) message-empty
 *    or literally "Script error." AND (b) carries no stack frames. A genuine
 *    same-origin app exception always carries a stack from our own code, so this
 *    can never hide a real bug — if any entry has a stack or a real message, the
 *    whole event passes through untouched.
 *
 * 2. The benign "ResizeObserver loop …" browser notice: fired when a
 *    ResizeObserver callback mutates layout faster than the browser can deliver
 *    notifications in one frame. It is a well-known non-error the spec emits for
 *    diagnostics — no exception propagates, no user flow breaks — but browsers
 *    surface it via window.onerror, so autocapture files it as an Issue. Dropped
 *    by an exact whole-message match on ANY entry (the industry-standard
 *    `ignoreErrors` move Sentry ships for exactly these strings), independent
 *    of stack, because the message itself is the reliable fingerprint.
 *
 * 3. Stack-bearing Cloudflare Turnstile client errors (e.g.
 *    `TurnstileError: [Cloudflare Turnstile] Error: 300030`): Turnstile runs on
 *    the worker site-entrance gate (/site/[token]) and the lead forms
 *    (components/ui/turnstile-widget.tsx). Unlike case (1), these arrive WITH a
 *    real stack trace pointing into Cloudflare's own bundle
 *    (challenges.cloudflare.com/turnstile/v0/api.js), so the case-(1) stackless
 *    filter lets them through and each variant forms its own phantom Issue.
 *    These retryable challenge-execution failures (`110600`, `110620`,
 *    `200500`, `300*`, and `600*`) live entirely inside Cloudflare's script and
 *    cannot be fixed from our code. Suppression requires BOTH a documented
 *    retryable error code AND an unmistakable Turnstile provenance signal.
 *    Configuration failures such as invalid/disabled site keys or unauthorized
 *    domains stay visible.
 *
 * All three are suppressed in `before_send` — the same mechanism that already
 * drops transient ChunkLoadError deploy-skew (lib/chunk-load-recovery.ts).
 */

interface ExceptionStackFrame {
  filename?: string;
  abs_path?: string;
  // posthog-js sends the raw source URL as `filename`; PostHog's ingestion
  // normalises it to `source`. before_send runs on the raw browser payload, but
  // matching `source` too future-proofs against SDK/normalisation changes.
  source?: string;
}

interface ExceptionListEntry {
  type?: string;
  value?: string;
  stacktrace?: { frames?: unknown[] } | null;
}

/** Minimal shape of a posthog-js CaptureResult we need for filtering. */
export interface ExceptionCaptureEvent {
  event?: string;
  properties?: {
    $exception_list?: ExceptionListEntry[];
  } & Record<string, unknown>;
}

/** The exact, redacted message browsers emit for a cross-origin script throw. */
const SCRIPT_ERROR_PATTERN = /^script error\.?$/i;

/**
 * The benign ResizeObserver loop notices browsers emit for diagnostics. Both
 * known variants are matched: the modern "loop completed with undelivered
 * notifications." and the older "loop limit exceeded". These never represent a
 * thrown exception or a broken flow — the observer simply rescheduled — so they
 * are pure noise in Error Tracking.
 *
 * Fully anchored to EXACTLY those two strings (like SCRIPT_ERROR_PATTERN, plus
 * an optional trailing dot): posthog-js strips any "Uncaught " prefix before
 * setting the entry value, so the notices land verbatim. A real app or library
 * error that merely mentions the observer (e.g. "ResizeObserver loop callback
 * failed", "Error in ResizeObserver loop handler: …") must NOT match, or an
 * actionable exception would be silently dropped.
 */
const RESIZE_OBSERVER_LOOP_PATTERN =
  /^resizeobserver loop (?:completed with undelivered notifications|limit exceeded)\.?$/i;

/**
 * Cloudflare's own Turnstile fingerprint. `TURNSTILE_MESSAGE_PATTERN` matches
 * the `[Cloudflare Turnstile]` tag Cloudflare prefixes onto every error it
 * throws (e.g. "[Cloudflare Turnstile] Error: 300030"), anywhere in the value.
 * `TURNSTILE_BUNDLE_PATTERN` matches a stack frame whose file is Cloudflare's
 * Turnstile bundle (challenges.cloudflare.com/turnstile/v0/api.js — the script
 * loaded by components/ui/turnstile-widget.tsx, optionally with a query string
 * and/or a further chunk path). Both are anchored to Cloudflare's own
 * type/message/host, so neither can match a same-origin app exception.
 */
const TURNSTILE_MESSAGE_PATTERN = /\[cloudflare turnstile\]/i;
const TURNSTILE_BUNDLE_PATTERN =
  /challenges\.cloudflare\.com\/turnstile\//i;
// Cloudflare documents the timeout codes 110600/110620, iframe-load failure
// 200500, and generic 300* / 600* challenge families as retryable. The last
// three digits of the generic families are internal and may vary. Do not
// suppress the other families: 110100/110110/110200 and 400020/400070, for
// example, identify actionable site-key, hostname, or disabled-key faults.
const RETRYABLE_TURNSTILE_CODE_PATTERN =
  /(?:^|\D)(?:110600|110620|200500|(?:300|600)\d{3})(?:\D|$)/;

function hasStackFrames(entry: ExceptionListEntry): boolean {
  const frames = entry.stacktrace?.frames;
  return Array.isArray(frames) && frames.length > 0;
}

/**
 * True when an exception-list entry is anonymized cross-origin noise: a
 * stackless entry whose message is empty or the browser's redacted
 * "Script error." placeholder. A stack — even on a "Script error." value —
 * means it is NOT redacted, so it is kept.
 */
function isAnonymousCrossOriginEntry(entry: ExceptionListEntry): boolean {
  if (hasStackFrames(entry)) return false;
  const value = typeof entry.value === "string" ? entry.value.trim() : "";
  return value === "" || SCRIPT_ERROR_PATTERN.test(value);
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is ENTIRELY
 * anonymized cross-origin noise (every entry message-empty/"Script error." AND
 * stackless) and should be dropped so it stops forming a phantom "(unknown
 * error)" Issue.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, and any exception where at least one entry carries a real
 * stack or a real message.
 */
export function isAnonymousCrossOriginException(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every(isAnonymousCrossOriginEntry);
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is a benign
 * "ResizeObserver loop …" browser notice and should be dropped so it stops
 * forming a phantom Issue. Matches on ANY entry's message (stack-independent) —
 * the message string is the reliable fingerprint, and the notice never carries
 * an actionable app stack.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, and any exception whose entries do not match.
 */
export function isBenignResizeObserverLoop(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.some(
    (entry) =>
      typeof entry.value === "string" &&
      RESIZE_OBSERVER_LOOP_PATTERN.test(entry.value),
  );
}

/**
 * True when an exception-list entry has unmistakable Cloudflare Turnstile
 * provenance: a `TurnstileError` type, a `[Cloudflare Turnstile]` message, or a
 * stack frame from Cloudflare's Turnstile bundle.
 */
function hasTurnstileProvenance(entry: ExceptionListEntry): boolean {
  if (typeof entry.type === "string" && entry.type.trim() === "TurnstileError") {
    return true;
  }
  if (
    typeof entry.value === "string" &&
    TURNSTILE_MESSAGE_PATTERN.test(entry.value)
  ) {
    return true;
  }
  const frames = entry.stacktrace?.frames;
  if (!Array.isArray(frames)) return false;
  return frames.some((frame) => {
    const { filename, abs_path, source } = (frame ?? {}) as ExceptionStackFrame;
    return [filename, abs_path, source].some(
      (path) => typeof path === "string" && TURNSTILE_BUNDLE_PATTERN.test(path),
    );
  });
}

/**
 * Suppress only retryable generic challenge failures with Turnstile
 * provenance. Requiring the numeric family is deliberate: provenance alone
 * also identifies actionable Cloudflare configuration errors that must reach
 * monitoring.
 */
function isRetryableTurnstileEntry(entry: ExceptionListEntry): boolean {
  return (
    hasTurnstileProvenance(entry) &&
    typeof entry.value === "string" &&
    RETRYABLE_TURNSTILE_CODE_PATTERN.test(entry.value)
  );
}

/**
 * PostHog `before_send` predicate: true when the `$exception` is a retryable
 * generic Cloudflare Turnstile challenge failure and should be dropped so it
 * stops forming a phantom Issue. Unlike `isAnonymousCrossOriginException`,
 * this catches the documented retryable Turnstile codes while preserving
 * actionable configuration families.
 *
 * Matches on ANY entry (stack-independent), because the fingerprint is
 * Cloudflare's own type/message/bundle, not the absence of a stack.
 *
 * Returns false (keep) for: non-exception events, an empty/absent
 * `$exception_list`, opaque Turnstile exceptions without a documented retryable
 * code, and configuration failures such as invalid keys or hostnames.
 */
export function isThirdPartyTurnstileException(
  event: ExceptionCaptureEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.some(isRetryableTurnstileEntry);
}
